import { computed, onMounted, onUnmounted } from 'vue'
import {
  type FetchLike,
  refreshReleaseState,
  releaseState,
} from '@/lib/appRelease'

const POLL_MS = 5 * 60 * 1000

/**
 * DEPLOY-FRESHNESS-001 — watch the SPA's own /version.json for a newer build.
 *
 * Compares the bundle's release id with the one the server currently serves (see
 * lib/appRelease.ts) every POLL_MS and whenever the tab becomes visible again. On a
 * mismatch `showUpdateBanner` turns on (App.vue: "A new version of the app is
 * available — Reload now"); the router additionally turns the next in-app navigation
 * into a full page load (router/releaseNavigationGuard.ts).
 *
 * Previously this compared VITE_APP_RELEASE_ID with the API's /app-meta — a check
 * coupled to the backend rollout that deploy never wired (both ids stayed empty).
 */
export function useAppReleaseWatcher(options: { fetchImpl?: FetchLike; pollMs?: number } = {}) {
  const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init))
  const pollMs = options.pollMs ?? POLL_MS

  const showUpdateBanner = computed(() => releaseState.updateAvailable)
  const clientReleaseId = computed(() => releaseState.clientReleaseId)
  const serverReleaseId = computed(() => releaseState.serverReleaseId)

  async function checkMismatch() {
    await refreshReleaseState(fetchImpl)
  }

  function reloadApp() {
    window.location.reload()
  }

  let intervalId: ReturnType<typeof setInterval> | null = null

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void checkMismatch()
    }
  }

  onMounted(() => {
    void checkMismatch()
    intervalId = setInterval(() => void checkMismatch(), pollMs)
    document.addEventListener('visibilitychange', onVisibility)
  })

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    document.removeEventListener('visibilitychange', onVisibility)
  })

  return {
    showUpdateBanner,
    clientReleaseId,
    serverReleaseId,
    reloadApp,
    checkMismatch,
  }
}
